import type { CandidateListing, CandidateView, FieldAction, FieldComparison, ResolutionKind, ResolutionRequest } from "../../../api/content/curationApi";

/**
 * What a curator chooses for one possible duplicate (curation-ui.md CU-6, after v1's
 * add / merge / reject with field actions): keep new (as a narrower term, or in another
 * module), merge into the target with a choice per field, or drop new. Pure.
 */
export interface ResolutionDraft {
  readonly kind: ResolutionKind;
  /** The proposal whose target a merge goes into, or a keep is about. */
  readonly candidateId: string;
  readonly fieldChoices: Readonly<Record<string, FieldAction>>;
  readonly narrower: boolean;
  readonly module: string | null;
  readonly reason: string;
}

const RDFS = "http://www.w3.org/2000/01/rdf-schema#";
const SKOS = "http://www.w3.org/2004/02/skos/core#";
const PREDICATE_NAMES: Readonly<Record<string, string>> = {
  [`${RDFS}label`]: "label",
  [`${SKOS}prefLabel`]: "label",
  [`${SKOS}altLabel`]: "altLabel",
  [`${SKOS}definition`]: "definition",
  "http://purl.obolibrary.org/obo/IAO_0000115": "definition",
  "http://purl.obolibrary.org/obo/IAO_0000112": "example",
  [`${RDFS}isDefinedBy`]: "definedBy",
  [`${RDFS}seeAlso`]: "seeAlso",
  [`${RDFS}comment`]: "comment",
  [`${SKOS}example`]: "example",
  [`${RDFS}subClassOf`]: "parent",
  [`${RDFS}subPropertyOf`]: "parent",
  [`${RDFS}domain`]: "domain",
  [`${RDFS}range`]: "range",
};

/** An i18n key under `reviews.curation.fields`, or `null` to show the IRI's local name. */
export function predicateKey(iri: string): string | null {
  return PREDICATE_NAMES[iri] ?? null;
}

/** The proposals for one term: open ones first, best score first. */
export function candidatesForTerm(listing: CandidateListing | null, term: string | null): readonly CandidateView[] {
  if (listing === null || term === null) {
    return [];
  }
  return listing.candidates
    .filter((view) => view.candidate.term === term)
    .sort((left, right) => Number(right.candidate.state === "open") - Number(left.candidate.state === "open") || right.candidate.scores.total - left.candidate.scores.total);
}

/** Field choices to start from: the server's defaults; identical fields are skipped. */
export function defaultFieldChoices(fields: readonly FieldComparison[]): Readonly<Record<string, FieldAction>> {
  return Object.fromEntries(fields.map((field) => [field.predicate, field.differs ? field.defaultAction : "skip"]));
}

/** The draft a proposal starts with: merge if the server suggests it, else keep new. */
export function initialDraft(view: CandidateView): ResolutionDraft {
  return {
    kind: view.suggestsMerge ? "merge_into" : "keep_new",
    candidateId: view.candidate.candidateId,
    fieldChoices: defaultFieldChoices(view.fields),
    narrower: false,
    module: null,
    reason: "",
  };
}

/** Moving to another target recomputes the field defaults, as v1 did. */
export function withTarget(draft: ResolutionDraft, view: CandidateView): ResolutionDraft {
  return { ...draft, candidateId: view.candidate.candidateId, fieldChoices: defaultFieldChoices(view.fields) };
}

/** Fields whose values differ: the only ones with a choice to make. */
export function differingFields(fields: readonly FieldComparison[]): readonly FieldComparison[] {
  return fields.filter((field) => field.differs);
}

export function resolutionRequest(draft: ResolutionDraft, head: string): ResolutionRequest {
  return {
    expectedHead: head,
    kind: draft.kind,
    fieldChoices: draft.kind === "merge_into" ? Object.entries(draft.fieldChoices).map(([predicate, action]) => ({ predicate, action })) : [],
    narrower: draft.kind === "keep_new" && draft.narrower,
    module: draft.kind === "keep_new" ? draft.module : null,
    reason: draft.reason.trim(),
  };
}

/** A score as a whole percentage, or `null` if unknown. */
export function percent(score: number | null): string | null {
  return score === null ? null : `${Math.round(score * 100)}%`;
}
